import React from "react";
import "./BankTable.css";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, forwardRef } from "react";
import { toast } from "react-toastify";
import {
  Viewicon,
  EditConvgIcon,
  DeleteConvgIcon,
} from "../../../../../Components/IconComponents";
import { useTranslation } from "react-i18next";
import Table from "../../../../../Components/UiComponents/Table/Table";
import { getData, postDataWithBody } from "../../../../../Services/Services";
import { setTableData } from "../../../../../CentralStore/ExportLMSTable/dataSlice";
import { getExportedColumnsData } from "../../../../../Components/UiComponents/utils";
import {
  getThemeURL,
  getallBanksByRole,
} from "../../../../../Services/EndPoints";
import Confirmpopup from "../../../Common/ConfirmPopup/Confirmpopup";
import { useDispatch } from "react-redux";
import Loader from "react-spinner-loader";
import { bankManagementcolumnsForExport } from "../../../../../JsonData/BankManagementConfig";

// Component for displaying and managing bank data in a table format
const BankTable = forwardRef((props) => {
  // Loading state for API calls
  const [loader, setLoader] = useState(false);

  // Main bank data array
  const [resourceData, setResourceData] = useState([]);
  // Filtered bank data based on search/district filter
  const [resourceSrchdData, setResourceSrchdData] = useState(null);
  // Total count of filtered rows
  const [rowCount, setRowCount] = useState(0);
  // Selected district filter value
  const [selectedDistrict, setSelectedDistrict] = useState("");
  // User permissions state
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Search data for table filtering
  const [searchData, setSearchData] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Update central store when search data changes
  useEffect(() => {
    sendDataToCentralStore(searchData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  // Re-filter data when district filter or resource data changes
  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistrict, resourceData]);

  // Filter bank data based on selected district
  const filterData = () => {
    let filtered = [...resourceData];

    // Filter by district
    if (selectedDistrict) {
      filtered = filtered.filter((bank) => bank.district === selectedDistrict);
    }

    setResourceSrchdData(filtered);
    setRowCount(filtered.length);
    sendDataToCentralStore(filtered);
  };

  // Parse web permissions from user profile
  const parseUserPermissions = (permissions) => {
    if (!permissions || !Array.isArray(permissions)) {
      return { canView: false, canEdit: false, canDelete: false };
    }

    // Find the permission with ID 75 for "All Banks" functionality
    const bankPermission = permissions.find(
      (permission) => permission.id === 75
    );

    if (!bankPermission || !bankPermission.web_permissions) {
      return { canView: false, canEdit: false, canDelete: false };
    }

    // Parse web_permissions string (e.g., "V,E,D" or "V" or "V,E")
    const webPermissions = bankPermission.web_permissions
      .split(",")
      .map((p) => p.trim());

    return {
      canView: webPermissions.includes("V"),
      canEdit: webPermissions.includes("E"),
      canDelete: webPermissions.includes("D"),
    };
  };

  // Initialize component - fetch user profile and show success message if any
  useEffect(() => {
    getUserProfile();
    if (props.locationInstance && props.locationInstance.msg !== "") {
      toast.success(props.locationInstance.msg, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000, // Close after 2 seconds
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user profile to determine role and permissions
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        // Check if user is SUPER ADMIN
        const isSuper = userProfile[0].role_name === "SUPER ADMIN";
        setIsSuperAdmin(isSuper);
        // Use the permissions array instead of web_permissions string
        const permissions = parseUserPermissions(userProfile[0].permissions);
        setUserPermissions(permissions);

        getBanksList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Create API payload based on user profile and role
  const createPayload = (profile) => {
    let admin_role;

    if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    } else if (profile.is_admin === true) {
      admin_role = "HO";
    } else {
      admin_role = "U";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  // // Navigate to add new bank page
  // const addNewHandler = () => {
  //   navigate("/branches/add-bank");
  // };

  // Fetch banks list based on user role and permissions
  const getBanksList = async (profile) => {
    setLoader(true);
    try {
      const payload = createPayload(profile);
      var bankList = await postDataWithBody(getallBanksByRole, payload, {});
      setLoader(false);
      setResourceData(bankList);
      setSelectedDistrict("");
      setRowCount(bankList.length);
      sendDataToCentralStore(bankList);
    } catch (error) {
      setLoader(false);
      console.error("Error fetching banks list:", error);
      toast.error("Failed to fetch banks list", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Send table data to Redux store for export functionality
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = bankManagementcolumnsForExport;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };

  // Define table columns with headers, accessors, and custom cell renderers
  const columns = React.useMemo(
    () => [
      {
        Header: t("banktableheaders.bankname"),
        accessor: "bankName",
        disableSortBy: true,
        // Truncate long bank names with tooltip
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("banktableheaders.ifscCode"),
        accessor: "ifscCode",
        disableSortBy: true,
        // Display IFSC code with tooltip
        Cell: (props) => {
          if (props.value) {
            return <span title={props.value}>{props.value}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("banktableheaders.mandal"),
        accessor: "mandal",
        disableSortBy: true,
        // Truncate long mandal names with tooltip
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("banktableheaders.city"),
        accessor: "city",
        disableSortBy: true,
        // Truncate long city names with tooltip
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("banktableheaders.district"),
        accessor: "district",
        disableSortBy: true,
        // Truncate long district names with tooltip
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },

      {
        Header: t("banktableheaders.contactName"),
        accessor: "contactName",
        disableSortBy: true,
        // Truncate long contact names with tooltip
        Cell: (props) => {
          if (props.value) {
            let descriptionText = props.value;
            if (props.value.length > 15) {
              descriptionText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{descriptionText}</span>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("banktableheaders.phoneNumber"),
        accessor: "phoneNumber",
        disableSortBy: true,
        // Display phone number with tooltip
        Cell: (props) => {
          if (props.value) {
            return <span title={props.value}>{props.value}</span>;
          } else {
            return "NA";
          }
        },
      },

      {
        Header: t("banktableheaders.activeStatus"),
        accessor: "activeStatus",
        disableSortBy: true,
        // Display active/inactive status with colored indicators
        Cell: (props) => {
          return props.value === true ? (
            <div
              className="status-indicator active"
              title="Active"
              style={{
                backgroundColor: "#15d1a4",
                color: "white",
                width: "25px",
                height: "25px",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                marginLeft: "9px",
              }}
            >
              ✓
            </div>
          ) : (
            <div
              className="status-indicator inactive"
              title="Inactive"
              style={{
                backgroundColor: "#ff5d30",
                color: "white",
                width: "25px",
                height: "25px",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                marginLeft: "9px",
              }}
            >
              ✕
            </div>
          );
        },
      },
      // Only show actions column if user has at least one permission
      ...(userPermissions.canView ||
      userPermissions.canEdit ||
      userPermissions.canDelete
        ? [
            {
              Header: t("banktableheaders.action"),
              disableSortBy: true,
              // Render action buttons based on user permissions
              Cell: (props) => {
                // Local state for delete confirmation popup
                const [isdelete, setIsdelete] = useState(false);
                // Toggle delete confirmation popup
                const deletePopUp = () => {
                  setIsdelete(!isdelete);
                };
                // Handle bank deletion (currently commented out)
                const deleteTableDataById = async () => {
                  // let data = { id: id };
                  // var res = await putDataWithBody(deleteBankbyId, data, {});
                  // if (res.status == "success") {
                  //   // getBankList();
                  // }
                  // toast.success(res.msg, {
                  //   position: toast.POSITION.TOP_CENTER,
                  //   autoClose: 2000, // Close after 2 seconds
                  // });
                  setIsdelete(false);
                };

                return (
                  <div
                    className="usertable__actionsicon"
                    style={{ display: "flex", gap: "10px" }}
                  >
                    {/* View bank details button - only show if user has view permission */}
                    {userPermissions.canView && (
                      <span
                        onClick={() =>
                          navigate("/branches/view-bank-details", {
                            state: { data: props.row.original },
                            view: true,
                          })
                        }
                      >
                        <Viewicon
                          value={props.value}
                          className="cursor-pointer me-2"
                        />
                      </span>
                    )}

                    {/* Edit bank button - only show if user has edit permission */}
                    {userPermissions.canEdit && (
                      <span
                        className="bank__table--icons"
                        onClick={() =>
                          navigate("/branches/add-bank", {
                            state: {
                              data: props.row.original.id,
                              formDataToEdit: props.row.original,
                              edit: true,
                            },
                          })
                        }
                      >
                        <EditConvgIcon
                          className="cursor-pointer"
                          mr-1
                          value={props.value}
                        />
                      </span>
                    )}

                    {/* Delete bank button - only show if user has delete permission */}
                    {userPermissions.canDelete && (
                      <span
                        onClick={deletePopUp}
                        style={{
                          cursor: "not-allowed",
                          opacity: 0.5,
                          pointerEvents: "none", // disables clicks
                        }}
                      >
                        <DeleteConvgIcon
                          value={props.value}
                          className="cursor-pointer mr-1"
                        />
                      </span>
                    )}

                    {/* Delete confirmation popup */}
                    {isdelete && (
                      <Confirmpopup
                        show={isdelete}
                        title={t("deletepopuptext")}
                        message={t("deletepopupmessage")}
                        handleToggle={deletePopUp}
                        row={props.row}
                        name={""}
                        deleteRow={deleteTableDataById}
                        cancel={t("cancelbutton")}
                        action={t("actionbutton")}
                      />
                    )}
                  </div>
                );
              },
            },
          ]
        : // eslint-disable-next-line react-hooks/exhaustive-deps
          []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, userPermissions] // Add userPermissions as dependency
  );

  return (
    <div>
      <div className="create-user">
        {/* Loading spinner overlay */}
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Data"
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          {/* Header section with total count and district filter */}
          <div
            style={{
              display: "flex",
              marginBottom: !isSuperAdmin ? "-50pt" : "-70pt",
            }}
          >
            {/* Total banks counter */}
            <div
              style={{
                backgroundColor: "#1a7a73",
                color: "#fff",
                padding: "6px 12px",
                borderRadius: "4px",
                fontWeight: "bold",
                marginRight: "39px",
                marginBottom: "-106px",
                marginLeft: "20px",
                width: "116px",
                fontSize: "12px",
                height: "30px",
              }}
            >
              Total Banks: {rowCount}
            </div>
            {/* District filter dropdown - Only show for SUPER ADMIN */}
            {isSuperAdmin && (
              <div
                className="d-flex align-items-center"
                style={{ gap: "5px", marginLeft: "310px", zIndex: "1" }}
              >
                <label
                  style={{
                    width: "60px",
                    color: "#969FA8",
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    fontWeight: "400",
                  }}
                >
                  Sort by:
                </label>
                <select
                  className="pl-4 pr-8 border appearance-none text-sm w-36"
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  style={{
                    color: "#000000",
                    fontWeight: "400",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    height: "29px",
                    borderRadius: "4px",
                  }}
                >
                  <option value="">All Districts</option>
                  {/* Generate district options from unique districts in data */}
                  {Array.from(
                    new Set(resourceData.map((bank) => bank.district))
                  ).map((district, index) => (
                    <option key={index} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Main data table container */}
        <div className="datatable__container">
          {resourceData && (
            <Table
              data={
                resourceSrchdData != null ? resourceSrchdData : resourceData
              }
              // addButtonLabel={t("Add Bank")}
              // addNewHandler={addNewHandler}
              columns={columns}
              numberOfRows={10}
              showExport={false}
              setSearchData={setSearchData}
              showFilter={false}
              showReset={false}
              onExpand={false}
              rowCount={rowCount} // Pass rowCount to the Table component
              headerTableHeadStyle={{
                background: "#1a7a73",
                color: "#FFFFFF",
                position: "sticky", // Ensure sticky positioning
                top: 0,
                zIndex: 1000,
              }}
              tableStyle={{
                border: "1px solid  #f0f0f0",
                borderRadius: "4px",
                padding: "0px",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default BankTable;
